import { Component } from '@theme/component';

/**
 * Selector de formato (WEB-38): colección, pack padre o MEGA PACK.
 *
 * La opción elegida sustituye a la variante del formulario principal de la
 * sección (`product-form-component` del mismo producto), así que "Agregar al
 * carrito", la barra fija y "Comprar ahora" envían solo esa opción. No toca
 * los formularios de otros productos de la página (tarjetas, recomendados).
 *
 * El formulario del tema lee `FormData` en el submit, en la fase de captura
 * del documento, así que la variante se escribe al cambiar de opción (y al
 * conectar, por si el navegador restauró otra selección).
 *
 * La barra fija de compra (`sticky-add-to-cart`) muestra el título, el precio
 * y la imagen de la opción elegida.
 *
 * Tras elegir con ratón o toque se quita el foco del radio, para que las
 * flechas del teclado vuelvan a desplazar la página en vez de cambiar de
 * opción. Quien navega con teclado (Tab) mantiene las flechas del grupo.
 *
 * Emite `upsell:select` (burbujea) con { tier, productId, variantId, productTitle }.
 *
 * Analítica (WEB-45): al elegir una opción publica el evento de Shopify
 * `upsell_seleccion`, que el píxel "Eventos propios → GA4" (pixels/eventos-ga4.js)
 * manda a GA4 como `select_promotion`, y marca la sesión en Microsoft Clarity
 * (etiqueta `upsell_formato` y evento `upsell_elige_pack` / `upsell_elige_mega`).
 *
 * @extends {Component}
 */
class UpsellPackSelector extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {boolean} */
  #pointerSelection = false;

  /**
   * Estado original de la barra fija (producto de la página).
   * @type {{ title: string, priceHTML: string, imageSrc: string | null, imageSrcset: string | null, imageAlt: string | null, media: string | undefined } | null}
   */
  #original = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener('pointerdown', () => (this.#pointerSelection = true), { signal });
    this.addEventListener('keydown', () => (this.#pointerSelection = false), { signal });

    this.#saveOriginal();
    this.#observe();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#observer?.disconnect();
  }

  /**
   * El framework entrega el evento con `target` = el elemento que declara
   * `on:change` (el fieldset), no el radio, así que se lee el radio marcado.
   */
  handleChange() {
    const input = this.selectedInput;
    if (!input) return;

    this.#sync();

    if (this.#pointerSelection) {
      input.blur();
      this.#pointerSelection = false;
    }

    this.dispatchEvent(
      new CustomEvent('upsell:select', {
        bubbles: true,
        detail: {
          tier: input.dataset.tier,
          productId: input.dataset.productId,
          variantId: input.value,
          productTitle: input.dataset.productTitle ?? null,
        },
      })
    );

    this.#reportSelection(input);
  }

  /**
   * Publica la elección para analítica. Solo se llama cuando la clienta cambia de opción,
   * no al cargar la página.
   * @param {HTMLInputElement} input
   */
  #reportSelection(input) {
    const tier = input.dataset.tier ?? '';
    const current = this.querySelector('.upsell-option__input[data-tier="current"]');
    const shopify = /** @type {any} */ (window).Shopify;

    try {
      shopify?.analytics?.publish?.('upsell_seleccion', {
        formato: tier,
        producto_id: input.dataset.productId ?? '',
        producto: input.dataset.productTitle ?? '',
        variante_id: input.value,
        precio: Number(input.dataset.priceCents ?? 0) / 100,
        moneda: shopify?.currency?.active ?? '',
        desde_producto: current instanceof HTMLInputElement ? current.dataset.productTitle ?? '' : '',
        ubicacion: 'pagina-producto',
      });
    } catch (error) {
      console.warn('[upsell-pack-selector] analytics.publish', error);
    }

    // Clarity ya está cargado en la página (embed de la app); si no está, no se hace nada.
    const clarity = /** @type {any} */ (window).clarity;
    if (typeof clarity === 'function') {
      clarity('set', 'upsell_formato', tier);
      if (tier === 'pack' || tier === 'mega') clarity('event', `upsell_elige_${tier}`);
    }
  }

  /** @returns {HTMLInputElement | null} */
  get selectedInput() {
    return this.querySelector('.upsell-option__input:checked');
  }

  /** @returns {ParentNode} */
  get #section() {
    return this.closest('.shopify-section') ?? document;
  }

  /**
   * Formulario principal del producto de esta página, dentro de la misma sección.
   * @returns {HTMLElement | null}
   */
  get productForm() {
    return this.#section.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
  }

  /** @returns {HTMLElement | null} */
  get #stickyBar() {
    return this.#section.querySelector(`sticky-add-to-cart[data-product-id="${this.dataset.productId}"]`);
  }

  #sync() {
    const selected = this.selectedInput;
    const form = this.productForm?.querySelector('form');
    if (!selected || !form) return;

    const idInput = form.querySelector('input[name="id"]');
    if (idInput instanceof HTMLInputElement && idInput.value !== selected.value) {
      idInput.value = selected.value;
    }

    const productIdInput = form.querySelector('input[name="product-id"]');
    const { productId } = selected.dataset;
    if (productIdInput instanceof HTMLInputElement && productId && productIdInput.value !== productId) {
      productIdInput.value = productId;
    }

    this.#syncDisplay(selected);
  }

  #saveOriginal() {
    const sticky = this.#stickyBar;
    const image = sticky?.querySelector('.sticky-add-to-cart__image-img');
    const addToCart = this.productForm?.querySelector('add-to-cart-component');

    this.#original = {
      title: sticky?.querySelector('.sticky-add-to-cart__title')?.textContent ?? '',
      priceHTML: sticky?.querySelector('.sticky-add-to-cart__price')?.innerHTML ?? '',
      imageSrc: image?.getAttribute('src') ?? null,
      imageSrcset: image?.getAttribute('srcset') ?? null,
      imageAlt: image?.getAttribute('alt') ?? null,
      media: addToCart instanceof HTMLElement ? addToCart.dataset.productVariantMedia : undefined,
    };
  }

  /**
   * Barra fija e imagen de la animación "al carrito" con la opción elegida.
   * @param {HTMLInputElement} selected
   */
  #syncDisplay(selected) {
    const original = this.#original;
    if (!original) return;

    const isCurrent = selected.dataset.tier === 'current';
    const sticky = this.#stickyBar;

    const title = sticky?.querySelector('.sticky-add-to-cart__title');
    const nextTitle = isCurrent ? original.title : selected.dataset.productTitle;
    if (title && nextTitle != null && title.textContent !== nextTitle) title.textContent = nextTitle;

    const price = sticky?.querySelector('.sticky-add-to-cart__price');
    const template = selected.closest('.upsell-option')?.querySelector('template[data-sticky-price]');
    const nextPrice = isCurrent ? original.priceHTML : template instanceof HTMLTemplateElement ? template.innerHTML : null;
    if (price && nextPrice != null && price.innerHTML !== nextPrice) price.innerHTML = nextPrice;

    const image = sticky?.querySelector('.sticky-add-to-cart__image-img');
    const nextSrc = isCurrent ? original.imageSrc : selected.dataset.image ?? original.imageSrc;
    if (image instanceof HTMLImageElement && nextSrc && image.getAttribute('src') !== nextSrc) {
      if (isCurrent && original.imageSrcset) image.setAttribute('srcset', original.imageSrcset);
      else image.removeAttribute('srcset');
      image.setAttribute('src', nextSrc);
      image.setAttribute('alt', (isCurrent ? original.imageAlt : selected.dataset.imageAlt) ?? '');
    }

    const addToCart = this.productForm?.querySelector('add-to-cart-component');
    const nextMedia = isCurrent ? original.media : selected.dataset.image ?? original.media;
    if (addToCart instanceof HTMLElement && nextMedia && addToCart.dataset.productVariantMedia !== nextMedia) {
      addToCart.dataset.productVariantMedia = nextMedia;
    }
  }

  /**
   * Si el tema vuelve a pintar el formulario (cambio de variante, section rendering),
   * se vuelve a aplicar la opción elegida.
   */
  #observe() {
    const productForm = this.productForm;
    if (!productForm) return;

    // #sync solo escribe cuando el valor cambia, así que no se realimenta.
    this.#observer = new MutationObserver(() => this.#sync());
    this.#observer.observe(productForm, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['value'],
    });
  }
}

if (!customElements.get('upsell-pack-selector')) {
  customElements.define('upsell-pack-selector', UpsellPackSelector);
}
